/**
 * Reads. Every one of these is local-only — no network, no awaiting.
 *
 * They're written to be called from `useLiveQuery`, so a screen re-renders by
 * itself whenever sync writes something underneath it. That's what makes the
 * offline and online experiences literally the same code path.
 */

import Dexie from 'dexie'

import { db, distanceScaleOf, movedWeightKg,
  type Exercise, type Metric, type Plan, type PlanDay, type SetRow, type Session }
  from './index'
import type { DistanceScale } from '../lib/units'
import type { SessionTotals } from '../lib/sessionSummary'
import { countRounds } from '../lib/rounds'

const live = <T extends { deleted_at?: string | null }>(rows: T[]) =>
  rows.filter((r) => !r.deleted_at)

// --- sessions ---------------------------------------------------------------

export async function listSessions(limit = 50): Promise<Session[]> {
  const rows = await db.sessions.orderBy('started_at').reverse().limit(limit * 2).toArray()
  return live(rows).slice(0, limit)
}

/**
 * Sentinel discipline for anything read through `useLiveQuery`:
 *
 *   undefined = still loading   (useLiveQuery's own pending value)
 *   null      = loaded, nothing there
 *
 * These MUST NOT be conflated. `Array.find()` returns `undefined` when nothing
 * matches, which is byte-identical to "still loading" — so a screen that shows
 * a spinner while the value is undefined will spin forever once the answer is
 * legitimately "no rows". That is exactly what happened to the log screen: with
 * no workout in progress it sat on "Loading…" and the Start workout card was
 * unreachable.
 */
export async function getSession(id: string): Promise<Session | null> {
  const s = await db.sessions.get(id)
  return s && !s.deleted_at ? s : null
}

export async function getActiveSession(): Promise<Session | null> {
  const rows = await db.sessions.orderBy('started_at').reverse().limit(20).toArray()
  return live(rows).find((s) => !s.finished_at) ?? null
}

export async function getSessionSets(sessionId: string): Promise<SetRow[]> {
  const rows = await db.sets.where('session_id').equals(sessionId).toArray()
  return live(rows).sort((a, b) => a.order_index - b.order_index)
}

export type SessionSummary = SessionTotals & {
  session: Session
  setCount: number
  exercises: string[]
  /**
   * Which unit family the session's distance belongs to, or null if it had
   * none. `short` only when EVERY distance set in it is metre-counted — a
   * session that rowed 2 k and then ran 5 k has to be written in one unit, and
   * the run is the half that would read absurdly in metres.
   */
  distanceScale: DistanceScale | null
}

/** History-list rows: enough to show without opening each session. */
export async function summarizeSessions(limit = 50): Promise<SessionSummary[]> {
  const sessions = await listSessions(limit)
  if (sessions.length === 0) return []

  const ids = new Set(sessions.map((s) => s.id))
  const allSets = live(await db.sets.where('session_id').anyOf([...ids]).toArray())
  const exercises = await db.exercises.toArray()
  const nameById = new Map(exercises.map((e) => [e.id, e.name]))
  const byId = new Map(exercises.map((e) => [e.id, e]))
  // For the set logged a minute ago that hasn't synced: the server stamps
  // `effective_weight_kg` from the body weight of the day, and until it does
  // this is the best local stand-in.
  const bodyweightKg = (await latestWeighIn())?.weight_kg ?? null

  return sessions.map((session) => {
    const sets = allSets.filter((s) => s.session_id === session.id)
    const working = sets.filter((s) => !s.is_warmup)

    // Every measure the session produced, not just the one that suits a
    // barbell. Warm-ups are excluded from all of them for the reason they are
    // excluded from tonnage: they describe getting ready, not what was done.
    let distanceM = 0
    let durationSeconds = 0
    let reps = 0
    // `short` until something long-scale turns up. Starting from the strict
    // answer and relaxing it means a session with no distance at all stays
    // null, and one that mixes scales lands on `long` rather than on whichever
    // exercise happened to be logged first.
    let scale: DistanceScale | null = null
    for (const s of working) {
      if (s.distance_m) {
        distanceM += s.distance_m
        const own = distanceScaleOf(byId.get(s.exercise_id))
        scale = scale === null ? own : (scale === 'short' && own === 'short' ? 'short' : 'long')
      }
      if (s.duration_seconds) durationSeconds += s.duration_seconds
      if (s.reps) reps += s.reps
    }

    return {
      session,
      // A left and a right are one set. The work of both still counts below.
      setCount: countRounds(working),
      distanceM,
      durationSeconds,
      reps,
      distanceScale: scale,
      // The load, not the logged number. On an assist machine `weight_kg` is
      // the help taken off, so summing it raw counted weight that was REMOVED
      // as work done — and rewarded taking more of it.
      volumeKg: working.reduce(
        (sum, s) =>
          sum
          + (movedWeightKg(s, byId.get(s.exercise_id), bodyweightKg) ?? 0)
            * (s.reps ?? 0),
        0),
      // Deduped, in the order they were performed — this is the line that makes
      // a history row identifiable at a glance ("Squat, Bench, Row").
      exercises: [...new Set(sets.map((s) => s.exercise_id))]
        .map((id) => nameById.get(id))
        .filter((n): n is string => Boolean(n)),
    }
  })
}

// --- body weight ------------------------------------------------------------

/**
 * Weigh-ins, oldest first — the series the chart draws.
 *
 * Read from Dexie rather than from an endpoint so the chart is there in a
 * basement gym, and so a weight typed offline appears on it immediately
 * instead of after the next sync.
 *
 * Rows with no weight are dropped: `body_metrics` also carries body fat and a
 * free-form measurements blob, and an entry that recorded only a waist
 * measurement is not a point on a weight chart.
 */
export async function weighIns(): Promise<Metric[]> {
  const rows = await db.metrics.orderBy('measured_at').toArray()
  return live(rows).filter((m) => m.weight_kg != null)
}

/** The most recent weigh-in, or null. Loaded-and-empty is null, never undefined. */
export async function latestWeighIn(): Promise<Metric | null> {
  const rows = await weighIns()
  return rows.length ? rows[rows.length - 1] : null
}

// --- per-exercise -----------------------------------------------------------

export async function getExercises(): Promise<Exercise[]> {
  return db.exercises.orderBy('name').toArray()
}

/**
 * When each exercise was last trained: exercise id → ISO time of its newest set.
 *
 * One pass over the sets, newest first, keeping the first time each exercise
 * turns up. Warm-ups count — a session where you only warmed up on something is
 * still the last time you touched it.
 */
export async function lastTrainedByExercise(): Promise<Map<number, string>> {
  const rows = await db.sets.orderBy('performed_at').reverse().toArray()
  const out = new Map<number, string>()
  for (const s of rows) {
    if (s.deleted_at || out.has(s.exercise_id)) continue
    out.set(s.exercise_id, s.performed_at)
  }
  return out
}

/**
 * Cached plans. Already filtered server-side to the non-archived ones, and
 * refreshPlans() replaces the table wholesale, so whatever is here is what you
 * can start a workout from.
 */
export async function getPlans(): Promise<Plan[]> {
  return db.plans.toArray()
}

/** The plan day a session was started from, or null for a freestyle workout. */
export async function getPlanDay(planDayId: number | null): Promise<PlanDay | null> {
  if (!planDayId) return null
  const plans = await db.plans.toArray()
  for (const plan of plans) {
    const day = plan.days.find((d) => d.id === planDayId)
    if (day) return day
  }
  // The plan was archived after this workout started. The session is still
  // perfectly loggable — it just loses the prescription, which is better than
  // refusing to open.
  return null
}

/**
 * Sets for one exercise, newest first. Backed by the [exercise_id+performed_at]
 * compound index — the local mirror of the server's ix_sets_lookup.
 */
export async function exerciseHistory(
  exerciseId: number,
  limit = 500,
): Promise<SetRow[]> {
  const rows = await db.sets
    .where('[exercise_id+performed_at]')
    .between([exerciseId, Dexie.minKey], [exerciseId, Dexie.maxKey])
    .reverse()
    .limit(limit * 2)
    .toArray()
  return live(rows).slice(0, limit)
}

/**
 * The working sets from the last time this exercise was trained — the input to
 * the progressive-overload suggestion, and what gets shown inline while logging.
 *
 * "Last time" means the previous *session*, not the previous N sets: if you did
 * 5 sets last week you want all 5, not a window that happens to straddle two
 * workouts.
 */
export async function lastPerformance(
  exerciseId: number,
  excludeSessionId?: string,
): Promise<{ sets: SetRow[]; performedAt: string } | null> {
  const history = await exerciseHistory(exerciseId, 200)
  const working = history.filter((s) => !s.is_warmup)
  const previous = working.find((s) => s.session_id !== excludeSessionId)
  if (!previous) return null

  const sets = working
    .filter((s) => s.session_id === previous.session_id)
    .sort((a, b) => a.set_index - b.set_index)

  return { sets, performedAt: previous.performed_at }
}
